import type { OfferChannel } from '../offer/offer.interface.js'

/**
 * What caused a snapshot (TO-464): the event published when the version it
 * records went live. Stored as text with a CHECK built from this enum (ADR-0011),
 * so a new trigger is one enum line. Only `offer.sent` and `offer.updated` have a
 * producer today; the upstream re-render triggers are reserved (follow-up ticket).
 */
export enum TriggerEventType {
	OFFER_SENT = 'offer.sent',
	OFFER_UPDATED = 'offer.updated',
	LEGAL_HOTFIX_PUBLISHED = 'legal.hotfix_published',
	TEMPLATE_UPGRADE_PUBLISHED = 'template.upgrade_published',
	TENANT_BRAND_RERENDER_PUBLISHED = 'tenant.brand_rerender_published',
}

/**
 * An auditable offer snapshot — an immutable record written at render time that
 * captures what was shown for an offer plus a deterministic `snapshotSha256` for
 * tamper detection. Snapshots are never mutated or deleted, so there is no
 * soft-delete flag and no `updatedAt`.
 */
export interface IOfferSnapshot {
	offerSnapshotId: string
	tenantId: string
	offerId: string
	customerId: string
	/**
	 * Org unit of the offer's customer at render time (TO-388) — decides who may
	 * read the snapshot. `null` only on snapshots recorded before TO-388, which
	 * only tenant-wide callers see.
	 */
	organizationUnitId: string | null
	/** Sequential per offer, starts at 1. */
	renderVersionNumber: number
	renderedAt: Date
	htmlS3Key: string
	pdfS3Key: string
	personalizationPayload: Record<string, unknown>
	/** SHA-256 (lowercase hex, 64 chars) of the canonical snapshot content. */
	snapshotSha256: string
	/**
	 * Tamper evidence of the delivered documents (TO-464): SHA-256 (lowercase hex)
	 * of the stamped HTML / PDF bytes as stored, and the S3 object versions they
	 * were read from. Null on snapshots recorded before TO-464. NOT part of
	 * `snapshotSha256`.
	 */
	htmlSha256: string | null
	pdfSha256: string | null
	htmlS3VersionId: string | null
	pdfS3VersionId: string | null
	/** The event that published this version (TO-464); null before TO-464. */
	triggerEventType: TriggerEventType | null
	/** Id of that event (plain uuid, no FK — the audit log is another context). */
	triggerEventId: string | null
	/** The user whose send / edit produced this version; null when system-triggered. */
	triggeredByUserId: string | null
	/** The send's channels (TO-465) for an `offer.sent` snapshot, in declaration order; null otherwise. */
	sendChannels: OfferChannel[] | null
	createdAt: Date
}
